import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

export default function TipsImproveApproval() {
  return (
    <div className="bg-white text-[#0a2545]">
      {/* HERO SECTION */}
      <section
        className="relative h-[60vh] flex items-center justify-center text-center text-white"
        style={{
          backgroundImage:
            "linear-gradient(rgba(10,37,69,0.6), rgba(13,94,99,0.6)), url('/bl3.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
          className="px-6"
        >
          <h1 className="text-5xl font-extrabold drop-shadow-lg">
            5 Tips to Improve Your Car Loan Approval Chances
          </h1>
          <p className="mt-4 text-lg text-gray-200 max-w-2xl mx-auto">
            Smart strategies that make lenders say “yes” — even when your credit isn’t perfect.
          </p>
        </motion.div>
      </section>

      {/* CONTENT SECTION */}
      <section className="max-w-5xl mx-auto py-16 px-6 leading-relaxed">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-3xl font-bold mb-8 text-[#0a2545]"
        >
          Why Approvals Can Be Tricky
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-gray-700 mb-8"
        >
          Many Canadians struggle to get car loans approved due to strict credit policies and high
          interest rates. But the truth is — your credit score isn’t the only factor that matters.
          Lenders like <span className="text-teal-500 font-semibold">Yes Auto</span> look at your
          overall financial profile, income stability, and repayment consistency.
        </motion.p>

        {/* TIP 1 */}
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="bg-gradient-to-br from-[#0a2545] to-[#0d5e63] text-white p-8 rounded-3xl mb-10 shadow-lg hover:shadow-teal-400/50 transition"
        >
          <h3 className="text-2xl font-semibold mb-3">1️⃣ Check Your Credit Report Early</h3>
          <p className="text-gray-200">
            Before applying, request your credit report from agencies like Equifax or TransUnion.
            Spot errors or outdated information that could hurt your approval chances and dispute
            them early. Even a small correction can raise your score by a few points — which matters.
          </p>
        </motion.div>

        {/* TIP 2 */}
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="bg-gradient-to-br from-[#0b3b52] to-[#0e6b63] text-white p-8 rounded-3xl mb-10 shadow-lg hover:shadow-teal-400/50 transition"
        >
          <h3 className="text-2xl font-semibold mb-3">2️⃣ Lower Your Debt Ratio</h3>
          <p className="text-gray-200">
            Lenders want to see that you can manage existing debts before taking on more. Pay down
            high-interest loans or credit card balances. A lower debt-to-income ratio improves your
            risk profile and can unlock better interest rates.
          </p>
        </motion.div>

        {/* TIP 3 */}
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="bg-gradient-to-br from-[#0a2545] to-[#0d5e63] text-white p-8 rounded-3xl mb-10 shadow-lg hover:shadow-teal-400/50 transition"
        >
          <h3 className="text-2xl font-semibold mb-3">3️⃣ Choose a Realistic Vehicle Budget</h3>
          <p className="text-gray-200">
            The amount you apply for directly affects your approval odds. Applying for a smaller,
            more affordable vehicle shows financial awareness — and lenders value that. Once your
            credit improves, you can always upgrade later.
          </p>
        </motion.div>

        {/* TIP 4 */}
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="bg-gradient-to-br from-[#0b3b52] to-[#0e6b63] text-white p-8 rounded-3xl mb-10 shadow-lg hover:shadow-teal-400/50 transition"
        >
          <h3 className="text-2xl font-semibold mb-3">4️⃣ Get a Co-Signer</h3>
          <p className="text-gray-200">
            A co-signer with a stable financial record can dramatically boost your chances of
            approval. It signals to lenders that the loan is less risky, which often results in
            faster approvals and lower interest rates.
          </p>
        </motion.div>

        {/* TIP 5 */}
        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="bg-gradient-to-br from-[#0a2545] to-[#0d5e63] text-white p-8 rounded-3xl mb-10 shadow-lg hover:shadow-teal-400/50 transition"
        >
          <h3 className="text-2xl font-semibold mb-3">5️⃣ Apply with a Trusted Partner</h3>
          <p className="text-gray-200">
            Work with financing partners who understand your situation — like{" "}
            <span className="text-teal-400 font-semibold">Yes Auto</span>. We specialize in
            personalized approvals for all credit types, offering you flexibility, respect, and
            premium support every step of the way.
          </p>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="mt-16 text-center"
        >
          <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
            The road to approval doesn’t have to be stressful. At Yes Auto, we combine trust,
            technology, and transparency to make your journey smooth and rewarding.
          </p>
          <Link
            to="/financing"
            className="inline-block bg-teal-400 text-[#0a2545] px-10 py-4 rounded-full font-semibold shadow-lg hover:scale-[1.05] transition"
          >
            Apply for Financing
          </Link>
        </motion.div>
      </section>
    </div>
  );
}
